<template>
    <div>
        <v-overlay :value="loadingDashboard">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <v-container pa-3>
            <v-card class="pa-5 mb-5">
                <h2>Dashboard</h2>
                <div v-if="user && (user.company_role === 'owner' || user.role === 'admin')">
                    <v-row class="mt-2 align-baseline">
                        <v-col :cols="2">Start/end date:</v-col>
                        <v-col :cols="column / 2">
                            <vc-date-picker
                                v-model="startDate"
                                :max-date="new Date()"
                                :popover="{ placement: 'bottom', visibility: 'click' }"
                                color="gray"
                            >
                                <template v-slot="{ inputValue, inputEvents }">
                                    <v-text-field :value="`${inputValue}`" v-on="inputEvents" dense outlined />
                                </template>
                            </vc-date-picker>
                        </v-col>

                        <v-col :cols="column / 2">
                            <vc-date-picker
                                v-model="endDate"
                                :min-date="startDate"
                                :max-date="new Date()"
                                :popover="{ placement: 'bottom', visibility: 'click' }"
                                color="gray"
                            >
                                <template v-slot="{ inputValue, inputEvents }">
                                    <v-text-field :value="`${inputValue}`" v-on="inputEvents" dense outlined />
                                </template>
                            </vc-date-picker>
                        </v-col>
                        <v-col :cols="2" v-if="user.role === 'admin'" style="text-align: right;">Company:</v-col>
                        <v-col :cols="4">
                            <v-select
                                v-if="user.role === 'admin'"
                                v-model="selectedCompany"
                                placeholder="Choose a company"
                                :items="dropdownCompanies"
                                item-text="name"
                                item-value="id"
                                @change="onChangeCompany"
                                hide-details
                                dense
                                outlined
                            />
                        </v-col>
                    </v-row>
                    <div class="dashboard-table pa-3">
                        <v-row>
                            <v-col class="col-item col-right-item row-title" cols="3"
                                >Count of payments initiated:
                            </v-col>
                            <v-col class="col-item col-right-item col-left-item row-title" cols="1"
                                >{{ dashboard.initiated ? dashboard.initiated : "-" }}
                            </v-col>
                            <v-col class="col-item col-right-item row-title" cols="3">Count of payments started:</v-col>
                            <v-col class="col-item col-left-item col-right-item" cols="1">
                                {{ dashboard.started ? dashboard.started : "-" }}
                            </v-col>
                            <v-col class="col-item col-right-item row-title" cols="3"
                                >Count of payments finished:
                            </v-col>
                            <v-col class="col-item col-left-item" cols="1">
                                {{ dashboard.finished ? dashboard.finished : "-" }}
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col class="col-item col-right-item col-no-top-item row-title" cols="3"
                                >Sum of payments initiated:
                            </v-col>
                            <v-col class="col-item col-left-item col-right-item col-no-top-item" cols="1"
                                >{{ dashboard.initiated_sum ? `${dashboard.initiated_sum}€` : "-" }}
                            </v-col>
                            <v-col class="col-item col-right-item col-no-top-item  row-title" cols="3"
                                >Sum of payments started:
                            </v-col>
                            <v-col class="col-item col-left-item col-right-item col-no-top-item" cols="1"
                                >{{ dashboard.started_sum ? `${dashboard.started_sum}€` : "-" }}
                            </v-col>
                            <v-col class="col-item col-right-item col-no-top-item" cols="3"
                                >Sum of payments finished:
                            </v-col>
                            <v-col class="col-item col-left-item col-no-top-item" cols="1"
                                >{{ dashboard.finished_sum ? `${dashboard.finished_sum}€` : "-" }}
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col class="row-title" cols="4"></v-col>
                            <v-col class="col-item col-right-item col-no-top-item row-title" cols="3"
                                >Percentage of payments initiated:
                            </v-col>
                            <v-col class="col-item col-right-item col-left-item col-no-top-item" cols="1">
                                {{
                                    dashboard.waiting_percentage
                                        ? `${parseFloat(dashboard.waiting_percentage).toFixed(2)}%`
                                        : "-"
                                }}
                            </v-col>
                            <v-col class="col-item col-right-item col-no-top-item row-title" cols="3"
                                >Percentage of payments initiated:
                            </v-col>
                            <v-col class="col-item col-left-item col-left-item col-no-top-item" cols="1">
                                {{
                                    dashboard.finished_percentage_waiting
                                        ? `${parseFloat(dashboard.finished_percentage_waiting).toFixed(2)}%`
                                        : "-"
                                }}
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col class="row-title" cols="4"></v-col>
                            <v-col class="row-title" cols="4"></v-col>
                            <v-col class="col-item col-right-item col-no-top-item row-title row-title" cols="3">
                                Percentage of payments started:
                            </v-col>
                            <v-col class="col-item col-left-item col-no-top-item" cols="1">
                                {{
                                    dashboard.finished_percentage_started
                                        ? `${parseFloat(dashboard.finished_percentage_started).toFixed(2)}%`
                                        : "-"
                                }}
                            </v-col>
                        </v-row>
                    </div>
                </div>
            </v-card>
            <div v-if="Object.keys(dashboard).length > 0">
                <v-card class="pa-5 mb-5">
                    <highcharts :options="chartCountOptions" class="paymentCountChart pb-3"></highcharts>
                </v-card>
                <v-card class="pa-5 mb-5">
                    <highcharts :options="chartSumOptions" class="paymentSumChart pb-3"></highcharts>
                </v-card>
                <v-card class="pa-5 mb-3">
                    <highcharts :options="bankChartOptions" class="bankChart pb-3"></highcharts>
                </v-card>
            </div>
        </v-container>
    </div>
</template>

<script>
import { mapState } from "vuex";
import Store from "@/store";
import moment from "moment";
import router from "../../router";
import { Chart } from "highcharts-vue";

export default {
    name: "dashboard",
    components: {
        highcharts: Chart
    },

    data() {
        let self = this;
        return {
            startDate: moment()
                .subtract(1, "weeks")
                .toDate(),
            endDate: moment().toDate(),
            column: 4,
            selectedCompany: null,
            dropdownCompanies: [],
            chartCountOptions: {
                title: {
                    text: "Count of transactions"
                },
                yAxis: {
                    min: 0,
                    title: {
                        text: "Count of transactions"
                    }
                },
                xAxis: {
                    categories: [],
                    labels: {
                        allowOverlap: true
                    }
                },
                chart: {
                    type: "line"
                },
                plotOptions: {
                    series: {
                        cursor: "pointer",
                        events: {
                            click: function(event) {
                                let status;
                                if (event.point.color === "#0066ff") {
                                    status = "";
                                } else if (event.point.color === "#999999") {
                                    status = "waiting";
                                } else if (event.point.color === "#4CAF50") {
                                    status = "finished";
                                } else if (event.point.color === "#eec01f") {
                                    status = "started";
                                }
                                self.$router.push({
                                    name: "payment-history",
                                    params: {
                                        start_date: event.point.category,
                                        end_date: event.point.category,
                                        status: status
                                    }
                                });
                            }
                        }
                    }
                },
                series: []
            },
            chartSumOptions: {
                title: {
                    text: "Sum of transactions"
                },
                yAxis: {
                    labels: {
                        format: "{text}€"
                    },
                    min: 0,
                    title: {
                        text: "Sum of transactions"
                    }
                },
                xAxis: {},
                chart: {
                    type: "line"
                },
                plotOptions: {
                    series: {
                        cursor: "pointer",
                        events: {
                            click: function(event) {
                                let status;
                                if (event.point.color === "#0066ff") {
                                    status = "";
                                } else if (event.point.color === "#999999") {
                                    status = "waiting";
                                } else if (event.point.color === "#4CAF50") {
                                    status = "finished";
                                } else if (event.point.color === "#eec01f") {
                                    status = "started";
                                }
                                self.$router.push({
                                    name: "payment-history",
                                    params: {
                                        start_date: event.point.category,
                                        end_date: event.point.category,
                                        status: status
                                    }
                                });
                            }
                        }
                    }
                },
                tooltip: {
                    valueSuffix: "€",
                    valueDecimals: 2,
                },
                series: []
            },
            bankChartOptions: {
                chart: {
                    type: "column"
                },
                title: {
                    text: "Bank statistics"
                },
                subtitle: {
                    text: "Amount of payments made in each bank"
                },
                xAxis: {
                    categories: []
                },
                yAxis: {
                    min: 0,
                    title: {
                        text: "Count of payments"
                    }
                },
                plotOptions: {
                    series: {
                        cursor: "pointer",
                        events: {
                            click: function(event) {
                                let status;
                                let bank_name;
                                if (event.point.color === "#4CAF50") {
                                    status = "finished";
                                    bank_name = event.point.category;
                                } else if (event.point.color === "#eec01f") {
                                    status = "started";
                                    bank_name = event.point.category;
                                }
                                self.$router.push({
                                    name: "payment-history",
                                    params: {
                                        start_date: moment()
                                            .subtract(1, "weeks")
                                            .format("YYYY-MM-DD"),
                                        end_date: moment().format("YYYY-MM-DD"),
                                        bank_name: bank_name,
                                        status: status
                                    }
                                });
                            }
                        }
                    }
                },
                series: []
            }
        };
    },
    computed: {
        ...mapState(["dashboard", "user", "companies", "bank_names", "loadingDashboard"])
    },
    async mounted() {
        if (!this.user.id && this.$route.meta.requires_auth) await Store.dispatch("getLoggedUserData");
        this.selectedCompany = this.user.active_company;

        if (this.user.company_role === "owner" || this.user.role === "admin") {
            await this.callNewDataFetch();
        } else if (this.user.role !== "admin" && this.user.company_role !== "owner") {
            await router.push("/admin/landing");
        }
        if (this.user.role === "admin") {
            await Store.dispatch("getCompanies");
            this.dropdownCompanies = [{ id: 0, name: "All companies" }, ...this.companies];
            this.company_id = this.user.user_companies[0].company_id;
            this.column = 4;
        } else {
            this.column = 5;
        }
    },
    watch: {
        startDate: async function() {
            await this.callNewDataFetch();
        },
        endDate: async function() {
            await this.callNewDataFetch();
        },
        dashboard: async function() {
            await this.getGraphInfo();
        }
    },
    methods: {
        getGraphInfo() {
            this.chartCountOptions.xAxis.categories = [];
            this.chartSumOptions.xAxis.categories = [];

            let statsLength = moment(this.endDate).diff(moment(this.startDate), "days") + 1;

            let waitingCountData = {
                name: "Waiting payments",
                data: [],
                color: "#999999"
            };
            let finishedCountData = {
                name: "Finished payments",
                data: [],
                color: "#4CAF50"
            };
            let startedCountData = {
                name: "Started payments",
                data: [],
                color: "#eec01f"
            };
            let initiatedCountData = {
                name: "Initiated payments",
                data: [],
                color: "#0066ff"
            };

            let waitingSumData = {
                name: "Waiting payments",
                data: [],
                color: "#999999"
            };
            let finishedSumData = {
                name: "Finished payments",
                data: [],
                color: "#4CAF50"
            };
            let startedSumData = {
                name: "Started payments",
                data: [],
                color: "#eec01f"
            };
            let initiatedSumData = {
                name: "Initiated payments",
                data: [],
                color: "#0066ff"
            };

            let startedBankData = {
                name: "Started",
                data: [],
                color: "#eec01f"
            };
            let finishedBankData = {
                name: "Finished",
                data: [],
                color: "#4CAF50"
            };

            let waitingCountList = {};
            let finishedCountList = {};
            let startedCountList = {};
            let initiatedCountList = {};

            let waitingSumList = {};
            let finishedSumList = {};
            let startedSumList = {};
            let initiatedSumList = {};

            let waitingBankList = {};
            let finishedBankList = {};
            let startedBankList = {};
            let emptyBankList = {};

            for (let i = 0; i < statsLength; i++) {
                let addedDay = moment(this.startDate)
                    .add(i, "days")
                    .format("YYYY-MM-DD");
                waitingCountList[addedDay] = 0;
                waitingSumList[addedDay] = 0;
            }

            let bankArray = this.dashboard.bank_array;

            for (let i = 0; i < Object.keys(bankArray).length; i++) {
                waitingBankList[Object.keys(bankArray)[i]] = 0;
            }
            startedCountList = { ...waitingCountList };
            initiatedCountList = { ...waitingCountList };
            finishedCountList = { ...waitingCountList };

            startedSumList = { ...waitingSumList };
            finishedSumList = { ...waitingSumList };
            initiatedSumList = { ...waitingSumList };

            startedBankList = { ...waitingBankList };
            finishedBankList = { ...waitingBankList };

            this.compactList(this.dashboard.waiting_array, waitingCountList, waitingSumList, waitingBankList);
            this.compactList(this.dashboard.finished_array, finishedCountList, finishedSumList, finishedBankList);
            this.compactList(this.dashboard.started_array, startedCountList, startedSumList, startedBankList);
            this.compactList(this.dashboard.initiated_array, initiatedCountList, initiatedSumList, emptyBankList);

            this.bankChartOptions.xAxis.categories = [];
            for (let i in bankArray) {
                if (startedBankList[i] !== 0 || finishedBankList[i] !== 0 || waitingBankList[i] !== 0) {
                    this.bankChartOptions.xAxis.categories.push(bankArray[i]);
                } else {
                    delete waitingBankList[i];
                    delete finishedBankList[i];
                    delete startedBankList[i];
                }
            }
            startedBankData.data = Object.values(startedBankList);
            finishedBankData.data = Object.values(finishedBankList);

            startedSumData.data = Object.values(startedSumList);
            finishedSumData.data = Object.values(finishedSumList);
            waitingSumData.data = Object.values(waitingSumList);
            initiatedSumData.data = Object.values(initiatedSumList);

            waitingCountData.data = Object.values(waitingCountList);
            finishedCountData.data = Object.values(finishedCountList);
            startedCountData.data = Object.values(startedCountList);
            initiatedCountData.data = Object.values(initiatedCountList);

            this.chartCountOptions.xAxis.categories = Object.keys(waitingCountList);
            this.chartCountOptions.series = [];

            this.chartSumOptions.xAxis.categories = Object.keys(waitingSumList);
            this.chartSumOptions.series = [];

            this.bankChartOptions.series = [];

            this.chartCountOptions.series[0] = waitingCountData;
            this.chartCountOptions.series[1] = finishedCountData;
            this.chartCountOptions.series[2] = startedCountData;
            this.chartCountOptions.series[3] = initiatedCountData;

            this.chartSumOptions.series[0] = waitingSumData;
            this.chartSumOptions.series[1] = finishedSumData;
            this.chartSumOptions.series[2] = startedSumData;
            this.chartSumOptions.series[3] = initiatedSumData;

            this.bankChartOptions.series[0] = finishedBankData;
            this.bankChartOptions.series[1] = startedBankData;
        },
        compactList(array, CountList, sumList, bankList) {
            let keys = Object.keys(CountList);
            let bankKeys = Object.keys(bankList);
            for (let j = 0; j < array.length; j++) {
                if (keys.includes(array[j]["date"])) {
                    CountList[array[j]["date"]]++;
                }
                if (keys.includes(array[j]["date"])) {
                    sumList[array[j]["date"]] += parseFloat(array[j]["amount"]);
                }
                if (bankKeys.includes(array[j]["swift"])) {
                    bankList[array[j]["swift"]]++;
                }
            }
        },
        async callNewDataFetch() {
            let data = {
                start_date: moment(this.startDate).format("YYYY-MM-DD"),
                end_date: moment(this.endDate).format("YYYY-MM-DD"),
                company_id: this.selectedCompany
            };
            await Store.commit("updateDashboardDateSearch", data);
        },
        onChangeCompany() {
            this.callNewDataFetch();
        }
    }
};
</script>
<style scoped>
.col-item {
    border: 1px solid lightgray;
}
.col-right-item {
    border-right-style: none;
}
.col-left-item {
    border-left-style: none;
}
.col-no-top-item {
    border-top-style: none;
}
</style>
